import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select, Stack, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { createPoi, createVisit, getCase, linkVisitPoi, listCases, listOrganizationMembers } from '../../api/resources';
import { POI_TYPE_VALUES } from '../../types/domain';
import { fieldOfficerUsers } from '../../lib/fieldOfficers';
import { userLabel } from '../../lib/labels';
import type { Visit } from '../../types/api';

const VISIT_TYPE_SUGGESTIONS = ['field_visit', 'verification', 'recovery', 'monitoring'] as const;

/** Sentinel option: enter a brand-new address rather than pick an existing one. */
const NEW_ADDRESS = '__new__';

export function CreateVisitModal({
    opened,
    onClose,
    caseId: fixedCaseId,
    onCreated,
}: {
    opened: boolean;
    onClose: () => void;
    /** When set, the visit is created for this case (no case picker). */
    caseId?: string;
    onCreated?: (visit: Visit) => void;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const casesQ = useQuery({
        queryKey: ['cases-all'],
        queryFn: () => listCases({ page: 1, limit: 500 }),
        enabled: opened && !fixedCaseId,
    });

    const form = useForm({
        initialValues: {
            caseId: fixedCaseId ?? '',
            visitType: 'field_visit',
            fieldOfficerId: '',
            /** '' = use the case default address; a POI id; or NEW_ADDRESS. */
            location: '',
            newPoiType: 'residence',
            newPoiAddress: '',
            newPoiName: '',
            instructions: '',
        },
        validate: {
            caseId: (v) => (fixedCaseId || v.trim() ? null : t('validation.caseRequired')),
            newPoiAddress: (v, values) =>
                values.location === NEW_ADDRESS && !v.trim()
                    ? t('features.visits.addressRequired')
                    : null,
        },
    });

    const selectedCaseId = fixedCaseId ?? form.values.caseId;

    /** The chosen case's addresses, so a visit can target a specific one. */
    const caseQ = useQuery({
        queryKey: ['case', selectedCaseId],
        queryFn: () => getCase(selectedCaseId),
        enabled: opened && Boolean(selectedCaseId),
    });

    // Assignable officers are field officers of the dispatch org
    // (execution org for commissioned cases, else the commissioning org).
    const dispatchOrganizationId =
        caseQ.data?.executionOrganizationId ?? caseQ.data?.organizationId;
    const membersQ = useQuery({
        queryKey: ['organization-members', 'dispatch', dispatchOrganizationId],
        queryFn: () =>
            listOrganizationMembers({
                page: 1,
                limit: 500,
                organizationId: dispatchOrganizationId!,
            }),
        enabled: opened && Boolean(dispatchOrganizationId),
    });

    const casePois = caseQ.data?.casePois ?? [];

    // Auto-select "monitoring" visit type when the case is a monitoring case.
    useEffect(() => {
        if (caseQ.data?.caseType?.toLowerCase() === 'monitoring') {
            form.setFieldValue('visitType', 'monitoring');
        }
        // Only react to the case loading, not form changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseQ.data?.id]);

    const createMutation = useMutation({
        mutationFn: async () => {
            const targetCaseId = selectedCaseId;
            const explicitLocation = form.values.location !== '';

            const body: Record<string, unknown> = {};
            if (form.values.visitType.trim()) {
                body.visitType = form.values.visitType.trim();
            }
            if (form.values.fieldOfficerId) {
                body.fieldOfficerId = form.values.fieldOfficerId;
            }
            if (form.values.instructions.trim()) {
                body.instructions = form.values.instructions.trim();
            }
            /** A chosen address is linked below, so suppress the backend auto-link. */
            if (explicitLocation) {
                body.skipAutoVisitPoi = true;
            }

            const visit = await createVisit(targetCaseId, body);

            if (form.values.location === NEW_ADDRESS) {
                const poi = await createPoi({
                    poiType: form.values.newPoiType,
                    claimedAddress: form.values.newPoiAddress.trim(),
                    name: form.values.newPoiName.trim() || undefined,
                });
                await linkVisitPoi(visit.id, { poiId: poi.id });
            } else if (explicitLocation) {
                await linkVisitPoi(visit.id, { poiId: form.values.location });
            }

            return visit;
        },
        onSuccess: (visit) => {
            notifications.show({ color: 'green', message: t('features.visits.created') });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
            if (fixedCaseId) {
                void queryClient.invalidateQueries({ queryKey: ['visits', 'for-case', fixedCaseId] });
                void queryClient.invalidateQueries({ queryKey: ['case', fixedCaseId] });
            }
            form.reset();
            form.setFieldValue('caseId', fixedCaseId ?? '');
            onClose();
            onCreated?.(visit);
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const fieldOfficers = fieldOfficerUsers(membersQ.data?.data ?? []);
    const caseOptions = (casesQ.data?.data ?? []).map((c) => ({
        value: c.id,
        label: [
            c.caseType
                ? t(`caseTypes.${c.caseType}`, { defaultValue: c.caseType })
                : undefined,
            c.externalReferenceId,
            c.id.slice(0, 8),
        ]
            .filter(Boolean)
            .join(' · '),
    }));

    const locationOptions = [
        ...casePois.map((link) => ({
            value: link.poiId,
            label:
                link.poi?.name ??
                link.poi?.claimedAddress ??
                link.poiId,
        })),
        { value: NEW_ADDRESS, label: t('features.visits.addNewAddress') },
    ];

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('features.visits.createModalTitle')}
            size="md"
        >
            <Stack>
                {!fixedCaseId ? (
                    <Select
                        label={t('fields.case')}
                        required
                        searchable
                        data={caseOptions}
                        value={form.values.caseId || null}
                        onChange={(v) => {
                            form.setFieldValue('caseId', v ?? '');
                            /** A carried-over address belongs to the previous case. */
                            form.setFieldValue('location', '');
                        }}
                        error={form.errors.caseId}
                    />
                ) : null}
                <Select
                    label={t('fields.visitType')}
                    searchable
                    data={VISIT_TYPE_SUGGESTIONS.map((visitType) => ({
                        value: visitType,
                        label: t(`visitTypes.${visitType}`, { defaultValue: visitType }),
                    }))}
                    value={
                        VISIT_TYPE_SUGGESTIONS.includes(
                            form.values.visitType as (typeof VISIT_TYPE_SUGGESTIONS)[number],
                        )
                            ? form.values.visitType
                            : null
                    }
                    onChange={(v) => form.setFieldValue('visitType', v ?? form.values.visitType)}
                    placeholder={form.values.visitType || undefined}
                />
                <TextInput
                    label={t('features.visits.visitTypeCustom')}
                    description={t('features.visits.visitTypeCustomHint')}
                    value={form.values.visitType}
                    onChange={(e) => form.setFieldValue('visitType', e.currentTarget.value)}
                />
                <Select
                    label={t('features.visits.location')}
                    description={t('features.visits.locationHint')}
                    placeholder={t('features.visits.locationDefault')}
                    clearable
                    searchable
                    disabled={!selectedCaseId}
                    data={locationOptions}
                    value={form.values.location || null}
                    onChange={(v) => form.setFieldValue('location', v ?? '')}
                />
                {form.values.location === NEW_ADDRESS ? (
                    <>
                        <Select
                            label={t('fields.poiType')}
                            required
                            data={POI_TYPE_VALUES.map((poiType) => ({
                                value: poiType,
                                label: t(`poiTypes.${poiType}`, { defaultValue: poiType }),
                            }))}
                            value={form.values.newPoiType}
                            onChange={(v) =>
                                form.setFieldValue('newPoiType', v ?? 'residence')
                            }
                        />
                        <TextInput
                            label={t('fields.address')}
                            required
                            value={form.values.newPoiAddress}
                            onChange={(e) =>
                                form.setFieldValue('newPoiAddress', e.currentTarget.value)
                            }
                            error={form.errors.newPoiAddress}
                        />
                        <TextInput
                            label={t('fields.name')}
                            placeholder={t('common.optional')}
                            value={form.values.newPoiName}
                            onChange={(e) =>
                                form.setFieldValue('newPoiName', e.currentTarget.value)
                            }
                        />
                    </>
                ) : null}
                <Select
                    label={t('fields.fieldOfficer')}
                    placeholder={t('features.visits.fieldOfficerOptional')}
                    clearable
                    searchable
data={fieldOfficers.map((u) => ({
                    value: u.id,
                    label: userLabel(u),
                }))}
                    value={form.values.fieldOfficerId || null}
                    onChange={(v) => form.setFieldValue('fieldOfficerId', v ?? '')}
                />
                <Textarea
                    label={t('fields.instructions')}
                    description={t('features.visits.instructionsHint')}
                    minRows={2}
                    value={form.values.instructions}
                    onChange={(e) => form.setFieldValue('instructions', e.currentTarget.value)}
                />
                <Button
                    loading={createMutation.isPending}
                    onClick={() => {
                        const result = form.validate();
                        if (!result.hasErrors) {
                            createMutation.mutate();
                        }
                    }}
                >
                    {t('common.actions.create')}
                </Button>
            </Stack>
        </Modal>
    );
}
